import { useEffect, useMemo, useState } from 'react';
import { Edit, Plus, Power, Trash2 } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { PageContainer } from '../../../shared/components/PageContainer';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { FEATURES } from '../../../shared/lib/permissions';
import { mensagemDeErro } from '../api';
import { TemplateTask, TerminationTemplate } from '../api/desligamento';
import { PessoasModal } from '../components/PessoasModal';
import { useColaboradores } from '../hooks';
import { useModelosDesligamento, useSalvarModelo, useSituacaoModelo } from '../hooks/desligamento';

const KIND_LABELS: Record<string, string> = { gestor: 'Gestor imediato', usuario: 'Usuário específico', rh: 'Equipe de RH' };

/** Modelos de desligamento por tipo de vínculo (RN-GP-009-006/009). */
export default function ModelosDesligamentoPage() {
  const toast = useToast();
  const modelos = useModelosDesligamento(true);
  const situacao = useSituacaoModelo();
  const [editing, setEditing] = useState<TerminationTemplate | 'novo' | null>(null);

  async function alternar(m: TerminationTemplate) {
    try {
      await situacao.mutateAsync({ id: m.id, active: !m.active });
      toast.success(m.active ? 'Modelo inativado.' : 'Modelo reativado.');
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível alterar a situação.'));
    }
  }

  return (
    <PageContainer
      title="Modelos de desligamento"
      subtitle="Tarefas que cada área executa no desligamento, por tipo de vínculo. Ativos e liderados são sempre verificados pelo sistema."
      breadcrumbs={[{ label: 'Pessoas' }, { label: 'Modelos de desligamento' }]}
      action={
        <Can feature={FEATURES.pessoasDesligamentoModelos} action="create">
          <Button onClick={() => setEditing('novo')}><Plus className="mr-2 h-5 w-5" /> Novo modelo</Button>
        </Can>
      }
    >
      <div className="space-y-4">
        {(modelos.data ?? []).map((m) => (
          <section key={m.id} className={`rounded-lg border border-gray-200 bg-white p-4 ${m.active ? '' : 'opacity-60'}`}>
            <header className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">{m.name} <span className="text-sm font-normal text-gray-500">· {m.employmentType.toUpperCase()}</span></h2>
              <Can feature={FEATURES.pessoasDesligamentoModelos} action="edit">
                <span className="flex gap-1">
                  <button type="button" className="rounded p-1.5 text-gray-500 hover:bg-gray-100" onClick={() => setEditing(m)} aria-label={`Editar ${m.name}`}><Edit className="h-4 w-4" /></button>
                  <button type="button" className="rounded p-1.5 text-gray-500 hover:bg-gray-100" onClick={() => alternar(m)}
                    aria-label={m.active ? `Inativar ${m.name}` : `Reativar ${m.name}`}><Power className="h-4 w-4" /></button>
                </span>
              </Can>
            </header>
            <ul className="mt-2 text-sm text-gray-700">
              {m.tasks.map((t) => (
                <li key={t.id}>{t.area}: {t.title} — {KIND_LABELS[t.assigneeKind]}{!t.required && ' (opcional)'}</li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      <ModeloModal value={editing} onClose={() => setEditing(null)} onSaved={(msg) => { setEditing(null); toast.success(msg); }} />
    </PageContainer>
  );
}

const NOVA_TAREFA: TemplateTask = { area: '', title: '', required: true, assigneeKind: 'rh' };

function ModeloModal({ value, onClose, onSaved }: { value: TerminationTemplate | 'novo' | null; onClose: () => void; onSaved: (m: string) => void }) {
  const salvar = useSalvarModelo();
  const usuarios = useColaboradores({ limit: 200 });
  const [name, setName] = useState('');
  const [type, setType] = useState<'clt' | 'pj'>('clt');
  const [tasks, setTasks] = useState<TemplateTask[]>([]);
  const [error, setError] = useState<string | null>(null);
  const editing = value && value !== 'novo' ? value : null;
  const userOptions = useMemo(() => (usuarios.data?.data ?? []).map((u) => ({ value: u.id, label: u.name })), [usuarios.data]);

  useEffect(() => {
    if (!value) return;
    setName(editing?.name ?? '');
    setType(editing?.employmentType ?? 'clt');
    setTasks(editing ? editing.tasks.map((t) => ({ ...t })) : [{ ...NOVA_TAREFA }]);
    setError(null);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const setTask = (i: number, patch: Partial<TemplateTask>) => setTasks((ts) => ts.map((t, j) => (j === i ? { ...t, ...patch } : t)));

  async function submit() {
    if (!name.trim()) return setError('Informe o nome.');
    if (tasks.length === 0 || tasks.some((t) => !t.area.trim() || !t.title.trim())) return setError('Preencha área e título de cada tarefa.');
    if (tasks.some((t) => t.assigneeKind === 'usuario' && !t.assigneeUserId)) return setError('Escolha o usuário das tarefas por usuário.');
    try {
      await salvar.mutateAsync({
        id: editing?.id ?? null,
        payload: {
          name: name.trim(), employmentType: type,
          tasks: tasks.map((t, i) => ({
            area: t.area.trim(), title: t.title.trim(), required: t.required, assigneeKind: t.assigneeKind,
            assigneeUserId: t.assigneeKind === 'usuario' ? t.assigneeUserId ?? undefined : undefined, sortOrder: i + 1,
          })),
        },
      });
      onSaved(editing ? 'Modelo atualizado.' : 'Modelo criado.');
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível salvar o modelo.'));
    }
  }

  return (
    <PessoasModal open={value !== null} size="lg" title={editing ? `Editar — ${editing.name}` : 'Novo modelo de desligamento'}
      description="Alterar o modelo não muda desligamentos já iniciados." submitLabel="Salvar" isSubmitting={salvar.isPending}
      submitError={error} onClose={onClose} onSubmit={submit}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TextInput label="Nome" value={name} onChange={(e) => setName(e.target.value)} />
        <AutocompleteSelect label="Tipo de vínculo" options={[{ value: 'clt', label: 'CLT' }, { value: 'pj', label: 'PJ' }]} value={type}
          onChange={(v) => setType(v as 'clt' | 'pj')} />
      </div>
      <div className="space-y-3">
        {tasks.map((t, i) => (
          <div key={i} className="grid grid-cols-1 gap-2 rounded border border-gray-200 p-3 md:grid-cols-2">
            <TextInput label="Área" value={t.area} onChange={(e) => setTask(i, { area: e.target.value })} />
            <TextInput label="Tarefa" value={t.title} onChange={(e) => setTask(i, { title: e.target.value })} />
            <AutocompleteSelect label="Responsável" options={Object.entries(KIND_LABELS).map(([v, l]) => ({ value: v, label: l }))}
              value={t.assigneeKind} onChange={(v) => setTask(i, { assigneeKind: v as TemplateTask['assigneeKind'] })} />
            {t.assigneeKind === 'usuario' && (
              <AutocompleteSelect label="Usuário" options={userOptions} value={t.assigneeUserId ?? ''} onChange={(v) => setTask(i, { assigneeUserId: v })} />
            )}
            <div className="flex items-center justify-between md:col-span-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={t.required} onChange={(e) => setTask(i, { required: e.target.checked })} /> Obrigatória
              </label>
              <button type="button" className="text-gray-500" aria-label="Remover tarefa" onClick={() => setTasks((ts) => ts.filter((_, j) => j !== i))}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        <Button size="sm" variant="secondary" onClick={() => setTasks((ts) => [...ts, { ...NOVA_TAREFA }])}><Plus className="mr-1 h-4 w-4" /> Tarefa</Button>
      </div>
    </PessoasModal>
  );
}
